import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  addMonths,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "lucide-react";
import { useEffect, useState } from "react";

interface CustomDatePickerProps {
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  placeholder: string;
  className?: string;
  unavailableDates?: Date[];
  minDate?: Date;
  autoOpenNext?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  isAdminContext?: boolean;
  disabled?: boolean;
  variant?: "outline" | "pill";
  showIcon?: boolean;
}

const CustomDatePicker = ({
  selected,
  onSelect,
  placeholder,
  className,
  unavailableDates = [],
  minDate,
  autoOpenNext,
  open: controlledOpen,
  onOpenChange,
  title,
  isAdminContext = false,
  disabled = false,
  variant = "outline",
  showIcon = false,
}: CustomDatePickerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  useEffect(() => {
    if (controlledOpen) {
      setCurrentMonth(selected ?? new Date());
    }
  }, [controlledOpen, selected]);

  const handleDateSelect = (date: Date | undefined) => {
    if (!date || disabled) return;

    const isUnavailable = unavailableDates.some((unavailableDate) =>
      isSameDay(startOfDay(unavailableDate), startOfDay(date)),
    );

    let isBeforeMin = false;
    if (!isAdminContext) {
      const today = startOfDay(new Date());
      const effectiveMinDate = minDate ? startOfDay(minDate) : today;
      isBeforeMin = isBefore(startOfDay(date), effectiveMinDate);
    }

    if (isUnavailable || isBeforeMin) return;

    onSelect(date);
    setOpen(false);
    if (autoOpenNext) setTimeout(autoOpenNext, 100);
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const getStartOfWeek = () => {
    const start = startOfMonth(currentMonth);
    const startOfWeek = new Date(start);
    startOfWeek.setDate(start.getDate() - start.getDay());
    return startOfWeek;
  };

  const getAllDaysToShow = () => {
    const startOfWeek = getStartOfWeek();
    return Array.from({ length: 42 }, (_, i) => {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      return day;
    });
  };

  const isBookedDate = (date: Date) =>
    unavailableDates.some((d) => isSameDay(startOfDay(d), startOfDay(date)));

  const isPastDate = (date: Date) => {
    if (isAdminContext) return false;
    const today = startOfDay(new Date());
    const effectiveMinDate = minDate ? startOfDay(minDate) : today;
    return isBefore(startOfDay(date), effectiveMinDate);
  };

  const displayText = selected ? format(selected, "dd/MM/yyyy") : placeholder;
  const monthYear = format(currentMonth, "MMMM yyyy", { locale: es });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant === "pill" ? "ghost" : "outline"}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            variant === "pill"
              ? "rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0"
              : "bg-white border-gray-200 h-12",
            !selected && "text-foreground font-medium",
            className,
          )}
        >
          {showIcon && variant === "pill" && (
            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
          )}
          {displayText}
        </Button>
      </PopoverTrigger>

      {!disabled && (
        <PopoverContent className="w-auto p-0 z-50" align="start">
          <div className="p-4 bg-white rounded-lg shadow-lg w-80">
            {title && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700">{title}</h3>
                {isAdminContext && (
                  <p className="text-xs text-brand-primary mt-1">
                    Modo admin: fechas pasadas permitidas
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-between mb-6">
              <button
                onClick={prevMonth}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <h2 className="text-lg font-semibold text-gray-900 capitalize">
                {monthYear}
              </h2>

              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
              {["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"].map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {getAllDaysToShow().map((day, index) => {
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isSelected = selected && isSameDay(day, selected);
                const isCurrentDay = isToday(day);
                const isBooked = isBookedDate(day);
                const isPast = isPastDate(day);
                const isDisabled = isBooked || isPast;

                return (
                  <button
                    key={index}
                    onClick={() => handleDateSelect(day)}
                    disabled={isDisabled}
                    className={cn(
                      "w-8 h-8 text-sm rounded-full transition-colors flex items-center justify-center",
                      !isCurrentMonth && "text-gray-300",
                      isCurrentMonth &&
                        !isDisabled &&
                        "hover:bg-gray-100 cursor-pointer",
                      isSelected && "bg-gray-900 text-white hover:bg-gray-900",
                      isCurrentDay &&
                        !isSelected &&
                        "bg-gray-100 text-gray-900",
                      isBooked &&
                        "text-gray-400 cursor-not-allowed bg-[repeating-linear-gradient(135deg,_rgba(156,163,175,0.15)_0_10%,_transparent_10%_20%)]",
                      isPast &&
                        !isBooked &&
                        "text-gray-400 bg-gray-100 cursor-not-allowed",
                    )}
                  >
                    {format(day, "d")}
                  </button>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default CustomDatePicker;
